import { useEffect, useState } from "react";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowDownCircle } from "lucide-react";
import { usePerms } from "@/context/PermContext";

const empty = { category: "Other", description: "", amount: "", payment_mode: "Cash", reference: "" };

export default function DebitTxns() {
  const { can } = usePerms();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("");

  const load = () => {
    api.get("/debit-txns").then((r) => setRows(r.data));
    api.get("/debit-categories").then((r) => setCats(r.data));
    api.get("/debit-txns/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.description || !form.amount) return toast.error("Description and amount required");
    try {
      await api.post("/debit-txns", { ...form, amount: parseFloat(form.amount) });
      toast.success("Debit transaction recorded");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = filter ? rows.filter((r) => r.category === filter) : rows;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-2xl font-semibold">Debit / Expense Transactions</h1>
          <p className="text-sm text-slate-500">Non-patient outgoings — kept separate from patient collections & refunds.</p>
        </div>
        <Button data-testid="debit-new-btn" onClick={() => setOpen(true)} className="bg-red-700 hover:bg-red-800 rounded-sm"><Plus className="w-4 h-4 mr-1"/> New Debit</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white border p-3">
            <div className="label-tiny">Total Debits</div>
            <div className="font-display text-xl font-semibold data-mono text-red-800">{fmtINR(summary.total)}</div>
          </div>
          {summary.by_category.slice(0, 4).map((c) => (
            <div key={c._id} className="bg-white border p-3">
              <div className="label-tiny">{c._id}</div>
              <div className="font-mono text-sm">{fmtINR(c.total)}</div>
              <div className="text-[10px] text-slate-500">{c.count} entries</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border p-3 flex gap-2 flex-wrap">
        <button onClick={() => setFilter("")} data-testid="debit-cat-ALL" className={`h-9 px-3 text-xs border rounded-sm ${!filter ? "border-red-700 bg-red-50 text-red-800 font-semibold" : "border-slate-300"}`}>ALL</button>
        {cats.map((c) => (
          <button key={c} data-testid={`debit-cat-${c}`} onClick={() => setFilter(c)} className={`h-9 px-3 text-xs border rounded-sm ${filter === c ? "border-red-700 bg-red-50 text-red-800 font-semibold" : "border-slate-300"}`}>{c}</button>
        ))}
      </div>

      <div className="bg-white border">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Date</th><th className="label-tiny">Txn No</th><th className="label-tiny">Category</th><th className="label-tiny">Description</th><th className="label-tiny">Mode</th><th className="label-tiny">Ref</th><th className="label-tiny">By</th><th className="label-tiny text-right">Amount</th></tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b" data-testid={`debit-row-${r.txn_no}`}>
                <td className="p-2 text-xs">{fmtDT(r.txn_date)}</td>
                <td className="font-mono text-xs">{r.txn_no}</td>
                <td><Badge className="rounded-sm bg-red-100 text-red-800">{r.category}</Badge></td>
                <td className="text-sm">{r.description}</td>
                <td className="text-xs">{r.payment_mode}</td>
                <td className="text-xs font-mono">{r.reference || "-"}</td>
                <td className="text-xs">{r.created_by_name}</td>
                <td className="text-right font-mono text-red-800">{fmtINR(r.amount)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500"><ArrowDownCircle className="w-8 h-8 mx-auto text-slate-300 mb-2"/>No debit transactions</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Debit Transaction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-9 border px-2 text-sm" data-testid="debit-form-cat">
                {cats.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Description*</Label><Textarea data-testid="debit-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (₹)*</Label><Input type="number" data-testid="debit-form-amt" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}/></div>
              <div><Label>Payment Mode</Label>
                <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="w-full h-9 border px-2 text-sm">
                  <option>Cash</option><option>UPI</option><option>Bank Transfer</option><option>Cheque</option><option>Card</option>
                </select>
              </div>
            </div>
            <div><Label>Reference (bill / invoice / voucher no.)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="debit-form-save" className="bg-red-700 hover:bg-red-800">Record Debit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
